import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {  MatChipInputEvent } from '@angular/material/chips';

@Component({
    selector: 'app-chips-input-form-field-ui',
    templateUrl: './chips-input-form-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./chips-input-form-field.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsInputFormFieldComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ChipsInputFormFieldComponent implements ControlValueAccessor {
    @Input() label: string;
    @Input() placeholder: string;
    @Input() showClear: boolean = true;
    @Input() showSearch: boolean = false;
    @Input() showSearchPulseAnim: boolean = false;
    @Input('class') classList: string;

    @Output() selectionChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
    @Output() triggerSearch: EventEmitter<void> = new EventEmitter<void>();
    @Output() searchChanged: EventEmitter<string> = new EventEmitter<string>();

    chips: Set<string> = new Set([]);

    private isSelectionChanged: boolean = false;
    private onChanged: (value: string[]) => void;
    private onTouched: () => void;

    addItem(event: MatChipInputEvent): void {
        const value: string = event.value?.trim();
        if (!this.chips.has(value) && !!value) {
            this.chips.add(value);
            this.onChangeSelection();
            event.chipInput!.clear();
            return;
        }
        if (!value && (this.chips.size || this.isSelectionChanged)) {
            this.onSearch();
        }
    }

    removeItem(chip: string): void {
        this.chips.delete(chip);
        this.onChangeSelection();
    }

    clearAll(): void {
        this.chips.clear();
        this.onChangeSelection();
    }

    onSearch(): void {
        this.isSelectionChanged = false;
        this.triggerSearch.emit();
    }

    writeValue(value: string[]): void {
        if (value?.length) {
            value.forEach((item: string) => this.chips.add(item));
            return;
        }
        this.chips.clear();
    }

    registerOnChange(fn: (color: string[]) => void): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private onChangeSelection(): void {
        const valueToEmit: string[] = Array.from(this.chips.values());

        this.selectionChanged.emit(valueToEmit);
        this.onChanged(valueToEmit);
        this.isSelectionChanged = true;
    }

    onSearchChange(event: Event): void {
        this.searchChanged.emit((event.target as HTMLInputElement).value);
    }
}
